import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Trash2, RotateCcw, Save, Users, Settings as SettingsIcon, DollarSign, Calendar, TrendingUp, X } from 'lucide-react';
import { toast } from 'sonner';
import { SwissCard, InlineSelect, StatusPill } from '../layout/SwissUI';

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/command-center/ops`;

const TIER_COLORS = {
  starter:    'bg-gray-100 text-gray-700 border-gray-300',
  plus:       'bg-blue-100 text-blue-700 border-blue-300',
  premier:    'bg-purple-100 text-purple-700 border-purple-300',
  essentials: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  executive:  'bg-amber-100 text-amber-800 border-amber-300',
  black_card: 'bg-gradient-to-r from-gray-900 to-black text-white border-black',
  retail:     'bg-gray-50 text-gray-500 border-gray-200',
};

const OpsLogV3Tab = () => {
  const [tiers, setTiers] = useState({});
  const [members, setMembers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [pl, setPl] = useState(null);
  const [ar, setAr] = useState(null);
  const [section, setSection] = useState('daily');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [newMember, setNewMember] = useState({ name: '', tier: 'starter', email: '', phone: '' });
  const [settingsDirty, setSettingsDirty] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [t, m, s, p, a] = await Promise.all([
        fetch(`${BASE}/membership-tiers`).then(r => r.json()),
        fetch(`${BASE}/members`).then(r => r.json()),
        fetch(`${BASE}/settings`).then(r => r.json()),
        fetch(`${BASE}/daily-pl?date=${date}`).then(r => r.json()),
        fetch(`${BASE}/ar-aging`).then(r => r.json()),
      ]);
      setTiers(t.tiers || {});
      setMembers(m.members || []);
      setSettings(s);
      setPl(p);
      setAr(a);
    } catch { toast.error('Failed to load Ops Log data'); }
  }, [date]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const addMember = async () => {
    if (!newMember.name) return toast.error('Name required');
    const r = await fetch(`${BASE}/members`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newMember),
    });
    if (r.ok) {
      toast.success('Member added');
      setNewMember({ name: '', tier: 'starter', email: '', phone: '' });
      loadAll();
    } else toast.error('Add failed');
  };

  const updateMember = async (id, patch) => {
    await fetch(`${BASE}/members/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    loadAll();
  };

  const removeMember = async (id) => {
    if (!window.confirm('Remove this member?')) return;
    await fetch(`${BASE}/members/${id}`, { method: 'DELETE' });
    toast.success('Member removed');
    loadAll();
  };

  const resetMemberHours = async (id) => {
    await fetch(`${BASE}/members/${id}/reset-hours`, { method: 'POST' });
    toast.success('Hours reset');
    loadAll();
  };

  const saveSettings = async () => {
    const r = await fetch(`${BASE}/settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
    });
    if (r.ok) { toast.success('Settings saved · P&L will recalc'); setSettingsDirty(false); loadAll(); }
    else toast.error('Save failed');
  };

  if (!pl || !settings) return <div className="p-6 text-sm text-gray-400">Loading Ops Log v3…</div>;

  const sections = [
    { key: 'daily',    label: 'Daily P&L',  icon: TrendingUp },
    { key: 'members',  label: 'Members + Hours Banks', icon: Users },
    { key: 'ar',       label: 'A/R Aging',  icon: DollarSign },
    { key: 'settings', label: 'Settings',   icon: SettingsIcon },
  ];

  return (
    <div data-testid="ops-log-v3-tab" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <span className="text-red-600">♠</span> Ops Log v3
          <span className="text-[10px] uppercase tracking-widest bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-bold">FOUNDATION</span>
        </h2>
        <div className="flex gap-2 border border-gray-200 rounded-lg p-1 bg-white">
          {sections.map(s => (
            <button key={s.key} onClick={() => setSection(s.key)}
              className={`text-xs font-bold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 ${section === s.key ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              data-testid={`ops-section-${s.key}`}>
              <s.icon className="h-3 w-3" /> {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* DAILY P&L */}
      {section === 'daily' && (
        <div className="space-y-4">
          <SwissCard className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-900">Daily P&L Roll-up</h3>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)}
                className="h-9 text-xs w-44" data-testid="daily-date-picker" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <Stat label="Bookings" v={pl.bookings_count} testId="stat-bookings" />
              <Stat label="Total Hrs" v={`${pl.total_hours}h`} testId="stat-hours" />
              <Stat label="Gross" v={fmt(pl.gross)} testId="stat-gross" green />
              <Stat label="Driver Pay" v={fmt(pl.driver_pay)} testId="stat-driver-pay" red />
              <Stat label="Net" v={fmt(pl.net)} testId="stat-net" green={pl.net > 0} red={pl.net < 0} />
              <Stat label={`Tax ${settings.tax_reserve_pct}%`} v={fmt(pl.tax_reserve)} testId="stat-tax-reserve" />
            </div>
            <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded flex items-center justify-between" data-testid="ace-keep-row">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">ACE Net (After Driver Pay & Tax Reserve)</p>
                <p className="text-[10px] text-emerald-700/70 mt-0.5">Settings: ${settings.driver_cost_per_hr}/hr driver · ${settings.fuel_cost_per_booking} fuel/booking · {settings.tax_reserve_pct}% tax reserve</p>
              </div>
              <p className="text-2xl font-black text-emerald-700">{fmt(pl.ace_keep)}</p>
            </div>
          </SwissCard>

          <SwissCard className="p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Bookings This Day ({pl.bookings_count})</h3>
            {pl.bookings_count === 0 ? (
              <p className="text-xs text-gray-400 py-6 text-center">No bookings for {pl.date}.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-left border-b border-gray-200">
                  <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                    <th className="py-2">Booking</th><th className="py-2">Client</th><th className="py-2">Vehicle</th><th className="py-2">Time</th><th className="py-2 text-right">Hrs</th><th className="py-2 text-right">Total</th><th className="py-2">Src</th>
                  </tr>
                </thead>
                <tbody>
                  {pl.bookings.map((b, i) => (
                    <tr key={i} className="border-b border-gray-100" data-testid={`daily-booking-row-${i}`}>
                      <td className="py-2 font-mono text-[10px]">{b.booking_id}</td>
                      <td className="py-2 font-semibold">{b.client_name || '—'}</td>
                      <td className="py-2 text-gray-600">{b.vehicle || '—'}</td>
                      <td className="py-2 text-gray-600">{b.pickup_time || '—'}</td>
                      <td className="py-2 text-right">{b.hours || 0}</td>
                      <td className="py-2 text-right font-bold">{fmt(b.total)}</td>
                      <td className="py-2"><span className="text-[9px] uppercase tracking-widest bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{b.source}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SwissCard>
        </div>
      )}

      {/* MEMBERS + HOURS BANKS */}
      {section === 'members' && (
        <div className="space-y-4">
          <SwissCard className="p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Add Member</h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
              <Input placeholder="Full Name" value={newMember.name} onChange={e => setNewMember(p => ({ ...p, name: e.target.value }))} className="h-9 text-xs" data-testid="new-member-name" />
              <Input placeholder="Email" value={newMember.email} onChange={e => setNewMember(p => ({ ...p, email: e.target.value }))} className="h-9 text-xs" data-testid="new-member-email" />
              <Input placeholder="Phone" value={newMember.phone} onChange={e => setNewMember(p => ({ ...p, phone: e.target.value }))} className="h-9 text-xs" data-testid="new-member-phone" />
              <select value={newMember.tier} onChange={e => setNewMember(p => ({ ...p, tier: e.target.value }))} className="h-9 text-xs border border-gray-200 rounded px-2" data-testid="new-member-tier">
                {Object.entries(tiers).filter(([k]) => k !== 'retail').map(([k, t]) => (
                  <option key={k} value={k}>{t.name} — ${t.monthly_fee}/mo</option>
                ))}
              </select>
              <Button onClick={addMember} size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs h-9" data-testid="add-member-btn">
                <Plus className="h-3 w-3 mr-1" /> Add Member
              </Button>
            </div>
          </SwissCard>

          <SwissCard className="overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">Members ({members.length})</h3>
              {members.length > 0 && (
                <Button size="sm" variant="outline" onClick={async () => {
                  if (!window.confirm('Reset ALL members hours banks? (typically run monthly)')) return;
                  const r = await fetch(`${BASE}/members/reset-all-hours`, { method: 'POST' }).then(r => r.json());
                  toast.success(`${r.members_reset} hours banks reset`);
                  loadAll();
                }} className="text-xs h-8" data-testid="reset-all-hours-btn">
                  <RotateCcw className="h-3 w-3 mr-1" /> Reset All Banks
                </Button>
              )}
            </div>
            {members.length === 0 ? (
              <p className="text-xs text-gray-400 p-8 text-center">No members yet. Add your first one above.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-left">
                  <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-2">Member</th><th className="px-4 py-2">Tier</th><th className="px-4 py-2 text-right">Rate</th>
                    <th className="px-4 py-2 text-right">Bank</th><th className="px-4 py-2 text-right">Used</th><th className="px-4 py-2 text-right">Left</th><th className="px-4 py-2 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => {
                    const usedPct = m.tier_meta.hours_included > 0 ? (m.hours_used_this_period / m.tier_meta.hours_included) * 100 : 0;
                    return (
                      <tr key={m.member_id} className="border-t border-gray-100" data-testid={`member-row-${m.member_id}`}>
                        <td className="px-4 py-2.5">
                          <p className="font-bold">{m.name}</p>
                          <p className="text-[10px] text-gray-500">{m.email || m.phone || m.member_id}</p>
                        </td>
                        <td className="px-4 py-2.5">
                          <InlineSelect
                            value={m.tier}
                            options={Object.entries(tiers).filter(([k]) => k !== 'retail').map(([k, t]) => ({ value: k, label: t.name }))}
                            onChange={(v) => updateMember(m.member_id, { tier: v })}
                            renderTrigger={(val) => {
                              const tierName = tiers[val]?.name || val;
                              const variant = val === 'black_card' ? 'active' : val === 'executive' ? 'success' : val === 'essentials' ? 'pending' : 'muted';
                              return <StatusPill variant={variant}>{tierName}</StatusPill>;
                            }}
                            testid={`tier-select-${m.member_id}`}
                          />
                        </td>
                        <td className="px-4 py-2.5 text-right">${m.tier_meta.hourly_rate}/hr</td>
                        <td className="px-4 py-2.5 text-right font-bold">{m.tier_meta.hours_included || '—'}{m.tier_meta.hours_included ? 'h' : ''}</td>
                        <td className="px-4 py-2.5 text-right">
                          <Input type="number" min="0" step="0.5" value={m.hours_used_this_period}
                            onChange={e => updateMember(m.member_id, { hours_used_this_period: parseFloat(e.target.value || 0) })}
                            className="h-7 text-xs w-16 ml-auto" data-testid={`hours-used-${m.member_id}`} />
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <span className={`font-bold ${m.hours_remaining === 0 && m.tier_meta.hours_included > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{m.hours_remaining}h</span>
                          {m.tier_meta.hours_included > 0 && (
                            <div className="w-20 h-1 bg-gray-200 rounded mt-1 ml-auto overflow-hidden">
                              <div className={`h-full ${usedPct >= 100 ? 'bg-red-500' : usedPct > 75 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(100, usedPct)}%` }} />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-1 justify-end">
                            <button onClick={() => resetMemberHours(m.member_id)} title="Reset hours" className="text-gray-400 hover:text-emerald-600" data-testid={`reset-member-${m.member_id}`}><RotateCcw className="h-3.5 w-3.5" /></button>
                            <button onClick={() => removeMember(m.member_id)} title="Remove" className="text-gray-400 hover:text-red-600" data-testid={`remove-member-${m.member_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </SwissCard>
        </div>
      )}

      {/* A/R AGING */}
      {section === 'ar' && (
        <SwissCard className="p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">Accounts Receivable</h3>
          <div className="grid grid-cols-3 gap-3 mb-5">
            <Stat label="Outstanding" v={fmt(ar?.outstanding || 0)} testId="ar-outstanding" />
            <Stat label="Overdue" v={fmt(ar?.overdue || 0)} testId="ar-overdue" red />
            <Stat label="Collected" v={fmt(ar?.collected || 0)} testId="ar-collected" green />
          </div>
          {ar && ar.invoices.length === 0 ? (
            <p className="text-xs text-gray-400 py-6 text-center">No invoices yet.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-left border-b border-gray-200">
                <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                  <th className="py-2">Invoice</th><th className="py-2">Client</th><th className="py-2 text-right">Amount</th><th className="py-2">Due</th><th className="py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {ar?.invoices.map((inv, i) => (
                  <tr key={i} className="border-b border-gray-100" data-testid={`ar-row-${i}`}>
                    <td className="py-2 font-mono text-[10px]">{inv.invoice_id || inv.invoiceId || '—'}</td>
                    <td className="py-2">{inv.client_name || inv.clientName || '—'}</td>
                    <td className="py-2 text-right font-bold">{fmt(inv.amount)}</td>
                    <td className="py-2 text-gray-500">{inv.due_date || inv.dueDate || '—'}</td>
                    <td className="py-2"><span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${
                      inv._aging_bucket === 'collected' ? 'bg-emerald-100 text-emerald-700'
                      : inv._aging_bucket === 'overdue' ? 'bg-red-100 text-red-700'
                      : 'bg-amber-100 text-amber-700'}`}>{inv._aging_bucket}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SwissCard>
      )}

      {/* SETTINGS */}
      {section === 'settings' && (
        <SwissCard className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-gray-900">Ops Settings & Defaults</h3>
              <p className="text-[10px] text-gray-500 mt-0.5">Drives daily P&L calc, default gratuity on quotes, member-tier discounts, and hours-bank reset day.</p>
            </div>
            <Button size="sm" onClick={saveSettings} disabled={!settingsDirty} className="bg-red-600 hover:bg-red-700 text-white text-xs h-9" data-testid="save-settings-btn">
              <Save className="h-3 w-3 mr-1.5" /> Save Settings
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              ['revenue_goal_monthly', 'Monthly Revenue Goal', '$', 0],
              ['tax_reserve_pct', 'Tax Reserve %', '%', 1],
              ['default_gratuity_pct', 'Default Gratuity %', '%', 1],
              ['default_sales_tax_pct', 'Sales Tax %', '%', 1],
              ['driver_cost_per_hr', 'Driver Cost / hr', '$', 0],
              ['fuel_cost_per_booking', 'Fuel Cost / booking', '$', 0],
              ['hours_bank_reset_day', 'Hours Bank Reset Day (1–28)', '#', 0],
            ].map(([k, label, prefix, dec]) => (
              <div key={k}>
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">{label}</p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{prefix}</span>
                  <Input type="number" step={dec === 1 ? '0.5' : '1'} min="0" value={settings[k]}
                    onChange={e => { setSettings(s => ({ ...s, [k]: parseFloat(e.target.value || 0) })); setSettingsDirty(true); }}
                    className="h-9 text-xs pl-7" data-testid={`setting-${k}`} />
                </div>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={settings.exotic_no_discount}
                onChange={e => { setSettings(s => ({ ...s, exotic_no_discount: e.target.checked })); setSettingsDirty(true); }}
                className="h-4 w-4 accent-red-600" data-testid="setting-exotic-no-discount" id="exoticchk" />
              <label htmlFor="exoticchk" className="text-xs"><b>Exotic = No Member Discount</b> <span className="text-gray-500">(all tiers pay retail on exotic fleet)</span></label>
            </div>
          </div>
        </SwissCard>
      )}
    </div>
  );
};

const Stat = ({ label, v, green, red, testId }) => (
  <div className="bg-gray-50 border border-gray-200 rounded p-3" data-testid={testId}>
    <p className="text-[9px] font-bold uppercase tracking-widest text-gray-500">{label}</p>
    <p className={`text-2xl font-black mt-1 ${green ? 'text-emerald-600' : red ? 'text-red-600' : 'text-gray-900'}`}>{v}</p>
  </div>
);

export default OpsLogV3Tab;
